<script lang="ts" setup>
import type { OtherServices_Plain } from '@webamboos/admin/src/components/sections/interfaces/OtherServices'
import { ref } from 'vue'

const props = defineProps<{
  data: OtherServices_Plain | undefined
}>()

const currentImage = ref<string>(props.data!.service[0]?.image?.url || '')
const activeServiceIndex = ref<number | null>(null)

const changeActiveImage = (url: string | undefined) => {
  currentImage.value = url || ''
}

const setActiveServiceIndex = (index: number | null) => {
  activeServiceIndex.value = index
}
</script>

<template>
  <Container
    v-if="data"
    class="screen-content group flex flex-col items-center space-y-12 pb-16 pt-32 text-center lg:space-y-16 lg:pb-32 lg:pt-48"
  >
    <div
      :class="{
        'lg:flex lg:justify-between': data.service.length > 1,
        'lg:flex lg:items-center lg:justify-center': data.service.length === 1,
      }"
      class="w-full space-x-28 lg:items-center"
    >
      <div :class="{ 'lg:w-1/2 lg:pr-12': data.service.length > 1 }" class="text-left">
        <Heading as="h2" size="caption" class="text-main-500">
          {{ data.eyebrow }}
        </Heading>
        <Heading as="h3" size="medium" class="mt-2">
          {{ data.title }}
        </Heading>
        <Paragraph class="mt-4 text-gray-600">
          {{ data.body }}
        </Paragraph>

        <div
          v-for="(service, index) in data.service"
          :key="index"
          class="hover-trigger group relative mb-8 first-of-type:mt-16"
          @mouseover="changeActiveImage(service.image?.url), setActiveServiceIndex(index)"
          @mouseleave="setActiveServiceIndex(null)"
        >
          <div class="relative flex items-start">
            <transition name="fade">
              <div
                v-if="activeServiceIndex === index"
                class="bg-main-500 absolute -left-4 top-2 h-[85%] w-1 lg:-left-8"
              ></div>
            </transition>
            <div class="relative">
              <span class="text-xl font-bold text-gray-900">
                {{ service.title }}
              </span>
              <p class="mt-2 text-sm text-gray-600">
                {{ service.body }}
              </p>

              <NuxtLink v-if="activeServiceIndex === index" :to="service.href">
                <span
                  class="text-main-600 group invisible flex items-center py-3 font-bold transition-opacity group-hover:visible group-hover:translate-x-1"
                  style="
                    transition:
                      opacity 0.3s ease,
                      transform 0.3s ease;
                  "
                >
                  {{ $t('workshop.learn_more') }}
                  <Icon name="lucide:move-right" size="18" class="ml-2 transition group-hover:translate-x-1" />
                </span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>

      <div :class="{ 'lg:w-1/2': data.service.length > 1 }" class="flex items-center justify-center lg:w-full">
        <NuxtImg
          v-if="currentImage"
          :src="currentImage"
          alt="Service Image"
          class="hidden min-h-[448px] min-w-[592px] max-w-md rounded-md border border-gray-200 p-2 shadow-lg lg:block"
        />
      </div>
    </div>
  </Container>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.group-hover\:translate-x-1 {
  transform: translateX(0.25rem);
}
.group-hover\:visible {
  visibility: visible;
}
</style>
